import { AccountResource, AssignmentResource, EventResource, Holiday, HolidayResource, Shift, ShiftResource, WorkPlanSummary } from "@apaq/orgkit-js";
import { PageRequest } from "@apaq/leap-data-core";
import { format, isSameDay } from "date-fns";
import { DateEvent, ofHoliday, ofShift } from "../../../../common/scheduler/date-event";
import log from 'loglevel';
import { Subject } from "rxjs";
import { bufferDebounce } from "src/app/common/observables/debounce-buffer";
import { DataSource } from "src/app/common/scheduler/datasource";
import { Resource } from "src/app/common/scheduler/resource";
import { ContextService, Roles } from "ngx-orgkit";


export class TimelineDataSource implements DataSource {
  private lastResourceIds?: string[];
  private lastFromDate?: Date;
  private lastToDate?: Date;
  private resourceChangedCallback?: () => void;
  private eventsChangedCallback?: (dates: Date[]) => void;
  private eventObservable = new Subject<Date[]>();

  constructor(private accountResource: AccountResource,
    private assignmentResource: AssignmentResource,
    private shiftResource: ShiftResource,
    private holidayResource: HolidayResource,
    private eventResource: EventResource,
    private contextService: ContextService) {

    this.eventResource.stream().on('assignment.plan-changed', ev => {
      log.debug('Event from server received(assignment.plan-changed)', ev);
      const dates: Date[] = [];
      const changeDetails = ev.data as any[];
      changeDetails.forEach(e => {
        if (e.previousDate != null && dates.indexOf(e.previousDate) < 0) {
          dates.push(e.previousDate);
        }
        if (e.newDate != null && dates.indexOf(e.newDate) < 0) {
          dates.push(e.newDate);
        }
      })

      if (dates.length > 0) {
        this.eventObservable.next(dates);
      }
    });

    this.eventObservable.pipe(bufferDebounce(500)).subscribe(datesArray => {
      if (this.eventsChangedCallback != null) {
        const dates: Date[] = [];
        // Merge date arrays and remove duplicate dates.
        datesArray.forEach(currentDates => currentDates.forEach(date => {
          if (dates.find(currentDate => isSameDay(currentDate, date)) == null) {
            dates.push(date);
          }
        }))
        this.eventsChangedCallback(dates);
        log.debug('eventsChanged: ', dates);
      }
    });

    contextService.contextChange.subscribe(_ => {
      if (this.resourceChangedCallback) {
        this.resourceChangedCallback();
      }
    });
  }

  onResourcesChanged(callback: () => void): void {
    this.resourceChangedCallback = callback;
  }

  onEventsChanged(callback: (dates: Date[]) => void): void {
    this.eventsChangedCallback = callback;
  }

  loadResources(): Promise<Resource[]> {
    const roles = this.contextService.account?.roles ?? [];
    const manager = roles.includes(Roles.ADMIN_ROLE) || Roles.hasPermissionsForOrganization(roles, this.contextService.organizationId!, Roles.ORGROLE_ADMIN) ||
      Roles.hasPermissionsForOrganization(roles, this.contextService.organizationId!, Roles.ORGROLE_MANAGEMENT);

    if (manager) {
      return this.accountResource.findAll({ size: 100, sort: 'name' }).then(result => {
        const resources: Resource[] = [];
        result.content.forEach(a => resources.push({ id: a.id, title: a.name }));
        return resources;
      });
    } else {
      const a = this.contextService.account;
      return Promise.resolve(a == null ? [] : [{ id: a.id, title: a.name }]);
    }
  }

  loadDateEvents(resourceIds: string[], from: Date, to: Date): Promise<DateEvent[]> {
    if (isSameDay(from, to)) {
      return Promise.resolve([]);
    }

    const strFrom = format(from, 'yyyy-MM-dd');
    const strTo = format(to, 'yyyy-MM-dd');

    const planPromise = this.assignmentResource.getSummaries(from, to, resourceIds);
    const shiftPromise = this.shiftResource.findAll({ query: { from: strFrom, to: strTo, resourceId: resourceIds }, size: 1000 } as PageRequest);
    const holidayPromise = this.holidayResource.findAll({ query: { resourceId: resourceIds, from: strFrom, to: strTo }, size: 1000 } as PageRequest);

    return Promise.all([planPromise, shiftPromise, holidayPromise]).then(dataArray => {
      const summaries: WorkPlanSummary[] = dataArray[0];
      const shifts: Shift[] = dataArray[1].content;
      const holidays: Holiday[] = dataArray[2].content;
      const events: DateEvent[] = [];

      // Adds Shifts and holidays
      shifts.filter(s => !s.working).map(s => events.push(ofShift(s)));
      holidays.map(h => events.push(ofHoliday(h)));

      summaries.forEach(s => {
        let type: 'info' | 'danger' | 'success' | 'warning' = 'info';
        const invalid = shifts.some(shift => !shift.working && shift.resourceId === s.accountId && isSameDay(s.date, shift.period.start));
        const shift = shifts.find(shift => shift.resourceId === s.accountId && isSameDay(s.date, shift.period.start))

        if (s.percentageComplete > 0 && s.percentageComplete < 100.0) {
          type = 'warning';
        }

        if (invalid) {
          type = 'danger';
        }

        if (s.percentageComplete === 100.0) {
          type = 'success';
        }

        const id = `${s.accountId}-${s.date.toISOString()}`;
        events.push({ id, title: s.cities.join(', '), start: s.date, resourceId: s.accountId, type, summary: s, shift } as DateEvent);
      })
      return events;
    });
  }
}